/**
 * Analysis — collection-rate projections over the same corpus the other two
 * tabs curate.
 *
 * Clips and Datasets answer "what do we have"; this tab answers "how fast is
 * it growing, and when could we ship X". Pick a slice of the fleet and it
 * shows the slice's recent capture rate, then projects every standard goal
 * against it at once — the rate is the fact, the goals are just divisions of
 * it.
 *
 * The corpus is /corpus (wh_capture), NOT the wearer-hands store the other two
 * tabs read. That store keeps only videos with hand detections and prunes to a
 * 14-day horizon, so a rate off it reported a third of the fleet and called it
 * the fleet. Here every video the fleet recorded is in scope and exactly one
 * filter applies — delivery grade, 1080p and >= 28 fps — which is the only cut
 * a lab actually cares about.
 *
 * Two modes. "All footage" rates everything the slice captures. "A bit of
 * everything" prices a BALANCED delivery instead: category-tagged footage
 * only, split evenly across the fastest K categories (K >= MIX_MIN_CATS,
 * whichever K maximizes throughput) — an everything-mix is throttled by the
 * slowest category it includes, not by the fleet's total, so its honest rate
 * is far below the headline one.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown, FiClock, FiCompass, FiTrendingUp } from 'react-icons/fi';

import { API_BASE_URL } from '../config';
import EnvActivityPanel from './EnvActivityPanel';
import ReportPanel from './ReportPanel';
import StatsPanel from './StatsPanel';
import {
  ACTIVITY_HIT_LIMIT,
  DeepScanJob,
  EnvCoverage,
  CorpusResponse,
  CorpusVideo,
  EnvLabel,
  SearchHit,
  TOP_ACTIVITIES,
  getDeepScan,
  runDescriptionSearch,
  startDeepScan,
} from './whShared';
import './AnalysisPage.css';

const GOAL_STOPS = [10, 50, 100, 500, 1000]; // projected collection goals, hours
// Tasks a lab is not buying, off by default here for the same reason as on the
// Datasets tab: they dominate the tag counts without being deliverable.
const DEFAULT_OFF_CATS = ['Driving', 'Using a phone'];
// Environments off by default, same reasoning as the tasks above: in-cab
// footage is not a site, and kiosk/ATM and residential interiors are not what a
// commercial enquiry is shopping for. `residential exterior & yard` is NOT in
// this list -- it is outdoor work at a residence (lawns, pest treatment, meter
// reads), which is ordinary commercial footage, unlike the inside of a home.
// Held here rather than inside the report so one control governs the rate, the
// stats and the report instead of three.
const DEFAULT_OFF_ENVS = [
  'vehicle interior',
  'payment kiosk & ATM',
  'residential interior',
  // Everything we cannot name a setting for, in one bucket (backend
  // UNCONFIRMED_ENV): not read yet, embeddings expired, or read and not
  // separable. Three labels for that used to sit on this list and nobody could
  // tell them apart.
  //
  // Off by default because that is where the bulk of it already was. Until
  // 2026-08-18 the 7,476 h that the Private Security industry prior invented a
  // setting for was labelled `vehicle interior`, the first entry on this list,
  // so leaving the merged bucket on would not be neutrality -- it would add that
  // footage to every default selection. The cost is that ~1,900 h which used to
  // show as `unclassified` is now hidden too; it carries its own count in the
  // Environments filter, so it is one tick away, and the warming part of it
  // reappears under real labels as classification finishes.
  'setting unknown',
];
const UNCLASSIFIED_ENV = 'setting unknown';
// Customers off by default, mirroring DEFAULT_OFF_ORG_NAMES on the Datasets tab
// so the two tabs price the same fleet. Matched on org NAME rather than id --
// the ids are auth0 subjects and must not be hardcoded here -- and normalised
// (lowercased, separators stripped) because the org is stored "DieboldNixdorf"
// while everyone writes "Diebold Nixdorf". A name with no footage yet costs
// nothing and holds the moment that org starts appearing.
const DEFAULT_OFF_ORG_NAMES = ['plix', 'Plix-sales', 'Diebold Nixdorf',
  'Accurate Adjustments', 'laz', 'IHTechLabs'];
const normOrg = (n: string) => n.toLowerCase().replace(/[\s_-]+/g, '');
// The rate window is the corpus window, not a second one. They used to differ
// (14-day rate over a 30-day corpus) and the card showed both, which read as
// two libraries rather than one number and its average. Sharing the window also
// makes the two lines check each other: corpus hours / window = the rate.
// Falls back to 30 only when the corpus payload has not arrived.
const DEFAULT_WINDOW_DAYS = 30;

// The floor on how many distinct categories an "everything" mix must draw
// from. Subjective by nature — the tab says which K it actually used.
const MIX_MIN_CATS = 10;

// A term's rate comes from the DEEP scan over this window, not the interactive
// search. The interactive depth (~6,000 frame-rows) tops out around ~140
// videos per term regardless of how much footage matches — measured on "cars"
// that undercounted the rate by ~20x, which turned a two-week collection into
// a phantom half-year. The deep scan walks the window to the quality floor
// (newest-first, so the trailing-14-day rate converges first), its partials
// are polled while it digs, and the rate can only rise as it runs. One scan
// per term; the server dedupes and reuses finished ones.
const TERM_WINDOW_DAYS = 30;

// How often the corpus is re-pulled while the server is still grading. Two
// orders slower than anything else this tab polls, and deliberately so: the
// payload is ~13 MB raw (2.2 MB gzipped, ~231k rows) against the environment
// map's few hundred KB, and a probe backlog is an hours-long job that moves the
// numbers by a fraction of a percent per minute. A cadence that felt "live"
// here would spend megabytes redrawing a rate to the same decimal place.
const CORPUS_POLL_MS = 60000;

// The environment poll has two cadences because there are two ways to be
// watching it. With Stats or the Report open the donut is on screen and being
// read tick by tick, which is what the original 8s was chosen for; with neither
// open the labels are only a filter dimension, and they just have to be right
// by the time someone clicks one.
const ENV_POLL_OPEN_MS = 8000;
const ENV_POLL_IDLE_MS = 30000;

// "plixXXXX-shift-20260805_131748[-joined].mp4" -> epoch ms, device-local
// clock — the same reading Clips uses for its rate. The /videos payload
// carries no capture field; the filename stamp is the only timestamp there is.
const captureMs = (filename: string): number | null => {
  const m = filename.match(/-(\d{8})_(\d{6})/);
  if (!m) return null;
  const [, d, t] = m;
  return new Date(
    +d.slice(0, 4), +d.slice(4, 6) - 1, +d.slice(6, 8),
    +t.slice(0, 2), +t.slice(2, 4), +t.slice(4, 6),
  ).getTime();
};

const fmtRate = (h: number) =>
  h >= 10 ? h.toFixed(0) : h >= 1 ? h.toFixed(1) : h.toFixed(2);

// Fleet-scale totals, where fmtHours' bare "7044h" loses a digit to the eye.
const fmtH = (h: number) => `${Math.round(h).toLocaleString()}h`;

// The corpus floor, for the "on hand" line. Parsed as local noon rather than
// through `new Date('YYYY-MM-DD')`, which is read as UTC midnight and prints as
// the day before for anyone west of Greenwich.
const fmtDay = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d, 12).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
};

// Under a day is said in hours: at fleet rates the small goals land within
// hours, and "<1 day" (or the old "already there") hid how many.
const fmtDays = (d: number) =>
  d < 1 / 24
    ? '<1 hour'
    : d < 1
      ? `~${Math.ceil(d * 24)} hours`
      : d > 400
      ? `~${(d / 365).toFixed(1)} years`
      : `~${Math.ceil(d).toLocaleString()} days`;

const dateAfter = (d: number) =>
  new Date(Date.now() + d * 86400e3).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

export default function AnalysisPage() {
  const [data, setData] = useState<CorpusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'all' | 'mix' | 'term'>('all');
  // Freeform term mode: what is typed, the term actually projected, and a
  // per-term cache of matched filenames — re-projecting "cars" after touching
  // the filters costs nothing, only a NEW term costs a scan. `done` means the
  // scan finished (the rate is the population's); until then it is a live
  // partial that only grows.
  const [termText, setTermText] = useState('');
  const [termQ, setTermQ] = useState<string | null>(null);
  const [termHits, setTermHits] = useState<
    Map<string, { fns: Set<string>; done: boolean }>
  >(new Map());
  const [termScan, setTermScan] = useState<{
    term: string;
    id: string;
    status: string;
    progress?: DeepScanJob['progress'];
  } | null>(null);
  // Exclude lists, not include lists: empty = everything selected, so an
  // industry or org that appears in the corpus tomorrow is counted by default
  // rather than silently dropped (same reasoning as the Datasets filters).
  const [excludedInds, setExcludedInds] = useState<string[]>([]);
  const [excludedOrgs, setExcludedOrgs] = useState<string[]>([]);
  // Tasks are a fleet-wide filter here, not a mix-only one: "what do we collect
  // per day" changes as much by dropping driving footage as by dropping a
  // customer. Same two off by default as the Datasets tab.
  const [excludedCats, setExcludedCats] = useState<string[]>(DEFAULT_OFF_CATS);
  const [excludedEnvs, setExcludedEnvs] = useState<string[]>(DEFAULT_OFF_ENVS);
  // The second tier, ON by default since 2026-08-18. It shipped off, on the
  // argument that 720p is gated out of every delivery so the headline numbers
  // must not quietly include footage a lab cannot be sent. What changed is what
  // the tab is mostly used for: "what do we have, and what happens in it" is now
  // asked here more often than "what can we ship", and under 1080p-only that
  // question is answered off a third of the corpus. Apartment complex exterior
  // is 1,419 videos at 1080p and 3,719 with both tiers -- the same environment,
  // two and a half times the evidence, and the two customers who shoot 720p
  // exclusively are absent rather than under-counted.
  //
  // Safe because `tierLabel` is derived from this same state and captions the
  // rate tile: with both on it reads "1080p and 720p, any frame rate", never
  // "delivery grade". Anyone pricing a delivery has to untick these two, and the
  // caption says so at the moment they look.
  const [include720, setInclude720] = useState(true);
  // The frame-rate gate, on by default for the same reason and flipped in the
  // same change. Independent of the resolution toggle rather than folded into
  // it: this is 72,930 videos and 29,187 h, more hours than the entire
  // delivery-grade corpus holds, and most of it is 1080p footage that fails on
  // frame rate alone. Which of the two cuts a buyer can live with is still the
  // buyer's answer, not ours -- the default just no longer assumes the question
  // being asked is a delivery one.
  const [includeLowFps, setIncludeLowFps] = useState(true);
  // Footage whose label the indoor/outdoor evidence contradicts. OFF by default:
  // the flag is 70% precise, so hiding it silently would drop correct footage
  // three times in ten. It is offered as a control because the useful question
  // is not "is every label right" (89%, measured) but "can I see which ones are
  // doubtful and drop them" -- excluding these takes the sample from 81% to 86%.
  const [hideContested, setHideContested] = useState(false);
  // Keep only footage from devices with a CONFIRMED IMU (hasIMU on the device
  // doc). Unconfirmed devices -- including ones that were offline or never
  // asked -- are excluded exactly like ones that answered "no".
  const [imuOnly, setImuOnly] = useState(false);
  // Corpus stats: what the selection HOLDS, next to what it collects. Moved here
  // from Datasets — it describes the corpus, not a delivery, and this tab
  // already owns a selection over that corpus.
  const [statsOpen, setStatsOpen] = useState(false);
  // The buyer-facing read of the same selection. Shares the environment cache
  // with Stats, so whichever opens first pays for it.
  const [reportOpen, setReportOpen] = useState(false);
  // The cross-tab. Reads the two maps this tab already holds -- no fetch of its
  // own -- so it costs nothing to have open beside the projections.
  const [crossOpen, setCrossOpen] = useState(false);
  // The Filters menu. Closes on a click outside it or Escape.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!filtersOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!filtersRef.current?.contains(e.target as Node)) setFiltersOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFiltersOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);
  const [envs, setEnvs] = useState<{ [fn: string]: EnvLabel }>({});
  const [envCoverage, setEnvCoverage] = useState<EnvCoverage | null>(null);

  // One corpus pull, shared by the cold-start ladder below it and the grading
  // refresh below that. Split out because the two callers differ only in what
  // they do with a `warming` answer -- the request, the status check and the
  // parse are identical, and two copies of them is how they drift apart.
  const fetchCorpus = useCallback(async (): Promise<CorpusResponse> => {
    const r = await fetch(`${API_BASE_URL}/api/wearer-hands/corpus`);
    if (!r.ok) throw new Error(`corpus fetch failed (${r.status})`);
    return r.json();
  }, []);

  // The corpus. A cold call answers `warming` while the server scans, so this
  // retries rather than rendering an empty fleet; once cached it is instant.
  useEffect(() => {
    let live = true;
    let tries = 0;
    const pull = async () => {
      try {
        const d = await fetchCorpus();
        if (!live) return;
        if (d.status === 'warming' && tries < 40) {
          tries += 1;
          setTimeout(pull, 6000);
          return;
        }
        setData(d);
      } catch (e: any) {
        if (live) setError(e.message);
      }
    };
    pull();
    return () => {
      live = false;
    };
  }, [fetchCorpus]);

  // Grading does not stop when the page finishes loading. The probe worker keeps
  // walking the ungraded backlog behind the tab, moving videos off grade -1 and
  // into or out of the delivery tier, so every number here is a floor that is
  // still rising. Pulling the corpus once on mount and never again meant a tab
  // left open all afternoon described the fleet as it stood the moment it
  // loaded: the "still grading N videos" note held the same N for hours while
  // the real N was falling to zero, and only a browser reload moved it.
  //
  // Gated on a boolean rather than on `data` itself so the timer is armed once
  // when grading starts and cleared once when it stops, instead of being torn
  // down and rebuilt by every payload that lands (and by every unrelated render
  // in between). `pending === 0` is the entire stop condition -- the backlog is
  // finished, and nothing moves again until an upload is probed, which the next
  // mount picks up.
  const corpusGrading = (data?.coverage?.pending ?? 0) > 0;
  useEffect(() => {
    if (!corpusGrading) return;
    let live = true;
    let inFlight = false;
    const t = setInterval(async () => {
      // A payload this size, served by a box that is also grading, can take
      // longer than the interval. Without this the ticks queue behind each
      // other and the tab spends the afternoon downloading the same 13 MB
      // repeatedly, which is the one way a 60s poll can still cost real money.
      if (inFlight) return;
      inFlight = true;
      try {
        const d = await fetchCorpus();
        // `warming` on a refresh means the server dropped its cache and is
        // rescanning. Keeping the corpus already on screen beats replacing a
        // whole fleet with a half-scanned one; the next tick takes the rebuild.
        if (live && d.status !== 'warming') setData(d);
      } catch {
        /* transient -- the interval is still armed, so the next tick retries */
      } finally {
        inFlight = false;
      }
    }, CORPUS_POLL_MS);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [corpusGrading, fetchCorpus]);

  // Environment labels back the second donut. Fetched on the first Stats open
  // rather than on mount: it is one entry per video in the whole store and no
  // other part of this tab reads it. Re-polled while the server is still
  // classifying, so the labels converge instead of freezing at a number that
  // has quietly stopped being true.
  const loadEnvs = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/wearer-hands/environments`);
      if (!r.ok) return;
      const d = await r.json();
      setEnvs(d.environments || {});
      setEnvCoverage(d.coverage || null);
    } catch {
      /* environments are additive; the task donut still works without them */
    }
  }, []);

  // On mount, not on first panel open: environments are a filter dimension now,
  // so the selection is wrong until they arrive.
  useEffect(() => {
    loadEnvs();
  }, [loadEnvs]);

  // Classification runs behind the tab the same way grading does, and on THIS
  // tab an environment is a filter dimension rather than panel decoration: an
  // unlabelled video reads as `unclassified`, so DEFAULT_OFF_ENVS quietly
  // under-applies until the labels land and the rate on screen is over a
  // selection that is still wrong. This used to be gated on `statsOpen ||
  // reportOpen`, which meant the main view -- where the rate and every
  // projection live -- never refreshed the labels at all; they froze at
  // whatever the mount fetch happened to see and stayed frozen until a reload.
  //
  // An interval keyed on two booleans, not the timeout chain this replaced: the
  // chain re-armed itself off each payload, so a single failed fetch (loadEnvs
  // swallows those by design) ended the polling for the life of the page, and
  // on the main view that page can be open for hours.
  const envsPending = (envCoverage?.pending ?? 0) > 0;
  const envPanelOpen = statsOpen || reportOpen;
  useEffect(() => {
    if (!envsPending) return;
    let inFlight = false;
    const t = setInterval(async () => {
      // Same reasoning as the corpus refresh: one entry per video in the whole
      // store is not a small response, and at the open-panel cadence a slow one
      // would otherwise have a second request stacked on it before it lands.
      if (inFlight) return;
      inFlight = true;
      try {
        await loadEnvs();
      } finally {
        inFlight = false;
      }
    }, envPanelOpen ? ENV_POLL_OPEN_MS : ENV_POLL_IDLE_MS);
    return () => clearInterval(t);
  }, [envsPending, envPanelOpen, loadEnvs]);

  // ---------- category tagging (mix mode only) ----------
  // The same activity queries Datasets warms, run lazily on the first switch
  // into mix mode — ~80 description searches is too much to pay for a tab
  // most visits leave in "all footage". Once started it runs to completion
  // even if the user toggles back, so the second visit is instant.
  const [activityHits, setActivityHits] = useState<{ [q: string]: SearchHit[] }>({});
  const [warmOn, setWarmOn] = useState(false);
  const warmStarted = useRef(false);
  useEffect(() => {
    // Task filtering applies in every mode now, so the hits are needed in
    // every mode -- not just when the mix is on screen.
    setWarmOn(true);
  }, [mode]);
  useEffect(() => {
    if (!warmOn || warmStarted.current) return;
    warmStarted.current = true;
    let stop = false;
    const queue = TOP_ACTIVITIES.flatMap((a) => a.queries);
    const fetchOne = async (q: string) => {
      try {
        // slim: nothing on this tab renders a hit's description text, and the
        // busiest query returns 10,210 of them at ~285 bytes each.
        const hits = await runDescriptionSearch(q, ACTIVITY_HIT_LIMIT, true);
        setActivityHits((p) => (q in p ? p : { ...p, [q]: hits }));
      } catch {
        setActivityHits((p) => (q in p ? p : { ...p, [q]: [] }));
      }
    };
    (async () => {
      await Promise.all(
        Array.from({ length: 4 }, async () => {
          while (queue.length && !stop) await fetchOne(queue.shift()!);
        }),
      );
    })();
    return () => {
      stop = true;
    };
  }, [warmOn]);

  const warming = useMemo(
    () =>
      TOP_ACTIVITIES.flatMap((a) => a.queries).filter((q) => !(q in activityHits))
        .length,
    [activityHits],
  );

  // Majority label per video, tie-broken by similarity — the same rule the
  // Datasets manifest uses, so "category" means the same thing on both tabs.
  const taskOf = useMemo(() => {
    const per = new Map<string, Map<string, { c: number; best: number }>>();
    TOP_ACTIVITIES.forEach((a) =>
      a.queries.forEach((q) =>
        (activityHits[q] || []).forEach((h) => {
          const m = per.get(h.filename) || new Map();
          const e = m.get(a.label) || { c: 0, best: 0 };
          e.c += 1;
          e.best = Math.max(e.best, h.similarity);
          m.set(a.label, e);
          per.set(h.filename, m);
        }),
      ),
    );
    const out = new Map<string, string>();
    per.forEach((m, fn) => {
      const top = Array.from(m.entries()).sort(
        (a, b) => b[1].c - a[1].c || b[1].best - a[1].best,
      )[0];
      if (top) out.set(fn, top[0]);
    });
    return out;
  }, [activityHits]);

  // Interned rows expanded once. Everything downstream reads these, never the
  // wire tuples.
  const allVideos = useMemo<CorpusVideo[]>(() => {
    if (!data) return [];
    return data.videos.map(([filename, oi, duration, grade]) => {
      const o = data.orgs[oi];
      return {
        filename,
        orgId: o?.[0] ?? '',
        orgName: o?.[1] ?? '',
        industry: o?.[2] ?? '',
        duration,
        grade,
      };
    });
  }, [data]);

  // The corpus proper: footage a lab could actually be sent, widened by whichever
  // of the two gates is switched off. ONE predicate, because the coverage strip
  // divides a subset of this by a superset of it -- when the two disagreed about
  // what counted, the share moved for reasons that had nothing to do with the
  // fleet.
  //
  // The toggles compose rather than override: 720p-and-low-fps (grade 4) needs
  // both, because it fails both gates. Ticking one box must never smuggle in
  // footage that also fails the other.
  const inTier = useCallback(
    (v: CorpusVideo) =>
      v.grade === 1
      || (include720 && v.grade === 2)
      || (includeLowFps && v.grade === 3)
      || (include720 && includeLowFps && v.grade === 4),
    [include720, includeLowFps],
  );

  // Everything below -- rate, filters, projections, stats, the report -- is
  // scoped to this list, so no number on the tab can disagree with another about
  // which footage it is describing.
  const videos = useMemo(() => allVideos.filter(inTier), [allVideos, inTier]);

  // What inTier currently means, in words. The rate tile and the coverage bar
  // both used to say "delivery grade" outright, which stopped being true the
  // moment a toggle could widen the selection underneath them -- and with two
  // toggles it can now be wrong in two different directions at once. Derived
  // from the same state as the predicate so the caption cannot drift from the
  // number it captions.
  const tierLabel = useMemo(() => {
    const res = include720 ? '1080p and 720p' : '1080p';
    if (!includeLowFps) return include720 ? res : 'delivery grade';
    return `${res}, any frame rate`;
  }, [include720, includeLowFps]);

  // How many videos the box would remove, counted over the same population the
  // rest of the card describes.
  const contestedCount = useMemo(
    () => videos.reduce((n, v) => n + (envs[v.filename]?.contested != null ? 1 : 0), 0),
    [videos, envs],
  );
  // Device = filename prefix (plix2494061e-shift-...), the same id the Devices
  // container is keyed on.
  const imuSet = useMemo(() => new Set(data?.imuDevices ?? []), [data]);
  const hasImu = useCallback(
    (v: CorpusVideo) => imuSet.has(v.filename.split('-', 1)[0]),
    [imuSet],
  );
  // How many of the menu's filters are off their defaults, shown on the button
  // so a narrowed population is visible with the menu closed.
  const filtersChanged =
    Number(!include720) + Number(!includeLowFps) + Number(hideContested) + Number(imuOnly);
  const imuNote =
    'Keeps only footage from devices confirmed to have an IMU (gyroscope). '
    + 'Devices not yet checked are left out.';
  const contestedNote =
    'A separate indoor/outdoor check disagrees with these labels. '
    + 'Hand-checked, 7 in 10 really are mislabelled -- usually the right subject '
    + 'shot outdoors, like a storefront tagged as a retail floor.';

  // Filename stamps parsed once; every filter click replays the rate math.
  const stamped = useMemo(
    () => videos.map((v) => ({ v, ts: captureMs(v.filename) })),
    [videos],
  );

  // Alphabetical for the same reason Datasets sorts this way: these lists are
  // for finding a specific name, and a volume ranking reshuffles under you.
  const industryRows = useMemo(() => {
    const n = new Map<string, number>();
    videos.forEach((v) => n.set(v.industry, (n.get(v.industry) || 0) + 1));
    return Array.from(n.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [videos]);

  const taskRows = useMemo(() => {
    const n = new Map<string, number>();
    TOP_ACTIVITIES.forEach((a) => n.set(a.label, 0));
    taskOf.forEach((label) => n.set(label, (n.get(label) || 0) + 1));
    return Array.from(n.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => [label, label, count] as const);
  }, [taskOf]);

  const envRows = useMemo(() => {
    const n = new Map<string, number>();
    videos.forEach((v) => {
      const k = envs[v.filename]?.environment || UNCLASSIFIED_ENV;
      n.set(k, (n.get(k) || 0) + 1);
    });
    return Array.from(n.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([k, c]) => [k, k, c] as const);
  }, [videos, envs]);

  const orgRows = useMemo(() => {
    const n = new Map<string, { name: string; n: number }>();
    videos.forEach((v) => {
      const e = n.get(v.orgId) || { name: v.orgName, n: 0 };
      e.n += 1;
      n.set(v.orgId, e);
    });
    return Array.from(n.entries()).sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [videos]);

  // ONE predicate, used by both halves of every ratio on this tab. It used to
  // be inlined into `selected` only, which left the coverage strip dividing a
  // filtered numerator by an unfiltered denominator: excluding Driving dropped
  // hours from the top of the fraction but not the bottom, so the share FELL as
  // task tagging warmed and the "this is a floor" note read as a lie.
  //
  // Split in two so the IMU panel can count both sides of its own box: every
  // filter except IMU, then IMU on top.
  const passesBaseFilters = useMemo(() => {
    const indOut = new Set(excludedInds);
    const orgOut = new Set(excludedOrgs);
    const catOut = new Set(excludedCats);
    // A task exclusion can only apply once the category hits are warmed; until
    // then taskOf is empty and every video is simply untagged. Untagged footage
    // is never excluded, which is what lets this run over the ungraded fleet.
    const envOut = new Set(excludedEnvs);
    return (v: CorpusVideo) =>
      !indOut.has(v.industry) &&
      !orgOut.has(v.orgId) &&
      !catOut.has(taskOf.get(v.filename) || '') &&
      !(hideContested && envs[v.filename]?.contested != null) &&
      !envOut.has(envs[v.filename]?.environment || UNCLASSIFIED_ENV);
  }, [excludedInds, excludedOrgs, excludedCats, excludedEnvs, taskOf, envs,
      hideContested]);
  const passesFilters = useCallback(
    (v: CorpusVideo) => passesBaseFilters(v) && !(imuOnly && !hasImu(v)),
    [passesBaseFilters, imuOnly, hasImu],
  );

  // Seed the customer exclusions once orgRows exist. Runs once: after that the
  // user owns the list, so re-seeding would silently undo their clicks.
  const orgsSeeded = useRef(false);
  useEffect(() => {
    if (orgsSeeded.current || orgRows.length === 0) return;
    orgsSeeded.current = true;
    const byName = new Set(DEFAULT_OFF_ORG_NAMES.map(normOrg));
    const ids = orgRows.filter(([, o]) => byName.has(normOrg(o.name))).map(([id]) => id);
    if (ids.length) setExcludedOrgs((prev) => Array.from(new Set([...prev, ...ids])));
  }, [orgRows]);

  // The slice everything on this tab describes: the rate, the projections, and
  // the stats panel all read this one list, so they can never disagree about
  // what "the selection" is.
  const selected = useMemo(
    () => stamped.filter(({ v }) => passesFilters(v)),
    [stamped, passesFilters],
  );

  const selectedVideos = useMemo(() => selected.map((s) => s.v), [selected]);

  const estimate = useMemo(() => {
    if (!data) return null;
    const vids = selected;
    const corpusHours = vids.reduce((a, { v }) => a + v.duration, 0) / 3600;

    const now = Date.now();
    const inWindow = (days: number) => {
      const cutoff = now - days * 86400e3;
      let hours = 0;
      let n = 0;
      const orgs = new Set<string>();
      vids.forEach(({ v, ts }) => {
        if (ts !== null && ts >= cutoff) {
          hours += v.duration / 3600;
          n += 1;
          orgs.add(v.orgId);
        }
      });
      return { hours, n, orgs: orgs.size };
    };

    // The RATE window, which is no longer the corpus span. The corpus opens on
    // the 1080p rollout and grows a day per day; dividing its hours by its span
    // would average in the days before the rollout, when the fleet shot nothing
    // deliverable, and report a rate the fleet has never had.
    const windowDays = data.rateDays ?? data.windowDays ?? DEFAULT_WINDOW_DAYS;
    const win = inWindow(windowDays);
    return {
      nVideos: vids.length,
      corpusHours,
      windowDays,
      // What the corpus HOLDS, for the "on hand" line only. Never a divisor.
      since: data.since,
      // Hours captured INSIDE the window, kept alongside the rate it divides
      // into. This — not corpusHours — is the number comparable to a fleet
      // total over the same days: corpusHours spans the whole store, which is
      // retained by upload time and so reaches further back than any window.
      windowHours: win.hours,
      windowVideos: win.n,
      perDay: win.hours / windowDays,
      windowOrgs: win.orgs,
    };
  }, [data, selected]);

  // Devices behind the rate: the same rows and window the rate card divides,
  // with every filter but IMU applied, so the panel can say how many of them
  // have an IMU whichever way its box is set.
  const imuDeviceCounts = useMemo(() => {
    if (!estimate) return null;
    const cutoff = Date.now() - estimate.windowDays * 86400e3;
    const all = new Set<string>();
    const withImu = new Set<string>();
    stamped.forEach(({ v, ts }) => {
      if (ts === null || ts < cutoff || !passesBaseFilters(v)) return;
      const dev = v.filename.split('-', 1)[0];
      all.add(dev);
      if (imuSet.has(dev)) withImu.add(dev);
    });
    return { all: all.size, withImu: withImu.size };
  }, [estimate, stamped, passesBaseFilters, imuSet]);

  // The reconciliation: a subtraction inside ONE corpus rather than a join
  // across two. Same rows, same window, same clock, same filters -- `grade` is
  // the only thing that separates the numerator from the denominator, which is
  // exactly what the strip claims to measure.
  const coverage = useMemo(() => {
    if (!estimate || !allVideos.length) return null;
    const cutoff = Date.now() - estimate.windowDays * 86400e3;
    let fleetHours = 0;
    let fleetVideos = 0;
    let gradeHours = 0;
    let unprobed = 0;
    allVideos.forEach((v) => {
      if (!passesFilters(v)) return;
      const ts = captureMs(v.filename);
      if (ts === null || ts < cutoff) return;
      fleetHours += v.duration / 3600;
      fleetVideos += 1;
      // Same predicate the corpus uses. Testing `=== 1` here while `videos`
      // accepted tier 2 would report the 720p hours in the numerator's absence
      // -- the share would fall the moment the toggle was switched on, which
      // reads as "including more footage found less of it".
      if (inTier(v)) gradeHours += v.duration / 3600;
      else if (v.grade === -1) unprobed += 1;
    });
    if (fleetHours <= 0) return null;
    return {
      fleetHours,
      fleetVideos,
      fleetPerDay: fleetHours / estimate.windowDays,
      share: Math.min(1, gradeHours / fleetHours),
      unprobed,
    };
  }, [allVideos, estimate, passesFilters, inTier]);


  // The balanced-mix rate. An even split over the K fastest categories moves
  // at K x (rate of the K-th fastest): adding a category widens the mix but
  // lowers the floor, so the tab takes whichever K >= MIX_MIN_CATS maximizes
  // throughput. Same formula against total (unwindowed) hours prices what the
  // corpus already holds in balanced form.
  const mix = useMemo(() => {
    if (!estimate || warming > 0) return null;
    const indOut = new Set(excludedInds);
    const orgOut = new Set(excludedOrgs);
    const byFn = new Map(stamped.map((s) => [s.v.filename, s]));
    const cutoff = Date.now() - estimate.windowDays * 86400e3;
    const winH = new Map<string, number>();
    const allH = new Map<string, number>();
    const catOut = new Set(excludedCats);
    taskOf.forEach((label, fn) => {
      const s = byFn.get(fn);
      if (!s) return;
      const { v, ts } = s;
      if (indOut.has(v.industry) || orgOut.has(v.orgId) || catOut.has(label)) return;
      allH.set(label, (allH.get(label) || 0) + v.duration / 3600);
      if (ts !== null && ts >= cutoff) {
        winH.set(label, (winH.get(label) || 0) + v.duration / 3600);
      }
    });
    const bestEven = (perCat: number[]) => {
      const sorted = perCat.slice().sort((a, b) => b - a);
      let best = { total: 0, k: 0 };
      for (let k = MIX_MIN_CATS; k <= sorted.length; k++) {
        const t = k * sorted[k - 1];
        if (t > best.total) best = { total: t, k };
      }
      return { ...best, available: sorted.length };
    };
    return {
      rate: bestEven(
        Array.from(winH.values()).map((h) => h / estimate.windowDays),
      ),
      corpus: bestEven(Array.from(allH.values())),
    };
  }, [estimate, warming, stamped, taskOf, excludedInds, excludedOrgs, excludedCats]);

  const runTerm = async () => {
    // Single term on purpose: one concept, one rate. A comma union would blur
    // whose rate is being projected, so anything after a comma is dropped.
    const t = termText.split(',')[0].trim().toLowerCase();
    if (!t) return;
    setMode('term');
    setTermQ(t);
    if (termHits.get(t)?.done) return; // full population already on hand
    // A scan for a term the user has moved past must stop burning database
    // throughput against the one they just asked for.
    if (
      termScan &&
      termScan.term !== t &&
      ['queued', 'running'].includes(termScan.status)
    ) {
      fetch(
        `${API_BASE_URL}/api/wearer-hands/videosearch/deep/${termScan.id}`,
        { method: 'DELETE' },
      ).catch(() => {});
    }
    try {
      const id = await startDeepScan(t, { days: TERM_WINDOW_DAYS });
      setTermScan({ term: t, id, status: 'queued' });
    } catch (e: any) {
      setError(e.message);
      setTermQ(null);
    }
  };

  // Poll the running scan's partial names — the same names=1 middle setting
  // the Datasets tab stops its scans with. Each tick replaces the term's hit
  // set, so the rate on screen converges toward the population's.
  useEffect(() => {
    if (!termScan || !['queued', 'running'].includes(termScan.status)) return;
    const t = setInterval(async () => {
      try {
        const j = await getDeepScan(termScan.id, false, true);
        if (j.hitNames) {
          const fns = new Set(j.hitNames);
          const done = j.status === 'done';
          setTermHits((p) => new Map(p).set(termScan.term, { fns, done }));
        }
        setTermScan((p) =>
          p && p.id === termScan.id
            ? { ...p, status: j.status, progress: j.progress }
            : p,
        );
      } catch (e: any) {
        if (String(e?.message).includes('unknown scan')) {
          setTermScan((p) =>
            p && p.id === termScan.id ? { ...p, status: 'error' } : p,
          );
          setError('scan lost (backend restarted?) — project again');
        }
        /* other poll failures are transient — keep polling */
      }
    }, 3000);
    return () => clearInterval(t);
  }, [termScan]);

  // Rate for the searched term: its matches, filtered to the selection, over
  // the same trailing windows the other modes use. All client-side off the
  // cached hit set.
  const termEstimate = useMemo(() => {
    if (!termQ) return null;
    const fns = termHits.get(termQ)?.fns;
    if (!fns) return null;
    const indOut = new Set(excludedInds);
    const orgOut = new Set(excludedOrgs);
    const vids = stamped.filter(
      ({ v }) =>
        fns.has(v.filename) && !indOut.has(v.industry) && !orgOut.has(v.orgId),
    );
    const corpusHours = vids.reduce((a, { v }) => a + v.duration, 0) / 3600;
    const now = Date.now();
    const inWindow = (days: number) => {
      const cutoff = now - days * 86400e3;
      let hours = 0;
      const orgs = new Set<string>();
      vids.forEach(({ v, ts }) => {
        if (ts !== null && ts >= cutoff) {
          hours += v.duration / 3600;
          orgs.add(v.orgId);
        }
      });
      return { hours, orgs: orgs.size };
    };
    const windowDays = TERM_WINDOW_DAYS;
    const win = inWindow(windowDays);
    return {
      nVideos: vids.length,
      corpusHours,
      windowDays,
      perDay: win.hours / windowDays,
      windowOrgs: win.orgs,
    };
  }, [termQ, termHits, stamped, excludedInds, excludedOrgs]);

  const toggle = (arr: string[], set: (v: string[]) => void, k: string) =>
    set(arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);

  if (error) return <div className="an-page an-error">{error}</div>;

  const mixWarming = mode === 'mix' && warming > 0;
  const termScanning =
    !!termScan &&
    termScan.term === termQ &&
    ['queued', 'running'].includes(termScan.status);
  // Waiting = no numbers to show at all. A scan with partials on hand shows
  // the live (converging) rate instead.
  const termWaiting = mode === 'term' && (!termQ || !termHits.get(termQ || ''));
  const effRate =
    mode === 'mix'
      ? mix?.rate.total ?? 0
      : mode === 'term'
        ? termEstimate?.perDay ?? 0
        : estimate?.perDay ?? 0;
  const effCorpus =
    mode === 'mix' ? mix?.corpus.total ?? 0 : estimate?.corpusHours ?? 0;

  const rateLine = !estimate
    ? 'measuring capture rate…'
    : mode === 'mix'
      ? mixWarming
        ? `tagging categories — ${warming} queries to go`
        : mix && mix.rate.total > 0
          ? `even split across the ${mix.rate.k} fastest categories ` +
            `(of ${mix.rate.available} with recent tagged footage) · ` +
            `trailing ${estimate.windowDays} days`
          : `only ${mix?.rate.available ?? 0} categories have recent tagged ` +
            `footage — a ${MIX_MIN_CATS}-category mix isn't collectable from ` +
            `this slice`
      : mode === 'term'
        ? !termQ
          ? 'type a term above and hit project'
          : !termEstimate
            ? termScanning
              ? `scanning the vector index for "${termQ}" — first numbers in ~10s…`
              : 'starting…'
            : (() => {
                const base =
                  `visual matches for "${termQ}" · across ` +
                  `${termEstimate.windowOrgs} org${
                    termEstimate.windowOrgs === 1 ? '' : 's'
                  } · trailing ${termEstimate.windowDays} days`;
                if (termScanning) {
                  const p = termScan?.progress;
                  return (
                    `${base} · still scanning` +
                    (p ? ` (${p.videos.toLocaleString()} videos so far)` : '') +
                    ' — the rate only rises from here'
                  );
                }
                if (termHits.get(termQ)?.done) {
                  return `${base} · complete population for the window`;
                }
                return `${base} · scan ${termScan?.status || 'stopped'} — showing what it found; project again to retry`;
              })()
        : estimate.perDay > 0
          ? `${tierLabel} · last ${estimate.windowDays} days`
          : `no ${tierLabel} capture in the last ${estimate.windowDays} days`;

  return (
    <div className="an-page">
      {/* What is being priced: everything the slice captures, or a balanced
          "bit of everything" delivery. A mode, not a filter — it changes what
          the rate MEANS, so it sits above the tiles it reinterprets. */}
      <div className="an-mode">
        <button
          type="button"
          className={mode === 'all' ? 'on' : ''}
          onClick={() => setMode('all')}
          title="Every hour the selected fleet captures"
        >
          All footage
        </button>
        <button
          type="button"
          className={mode === 'mix' ? 'on' : ''}
          onClick={() => setMode('mix')}
          title={`A balanced mix: category-tagged footage split evenly across at least ${MIX_MIN_CATS} categories — the honest rate for "add a bit of everything"`}
        >
          A bit of everything
        </button>
        <form
          className={`an-term${mode === 'term' ? ' on' : ''}`}
          onSubmit={(e) => {
            e.preventDefault();
            runTerm();
          }}
        >
          <input
            type="text"
            placeholder="one search term — e.g. cars"
            value={termText}
            onChange={(e) => setTermText(e.target.value)}
            onFocus={() => {
              if (termQ) setMode('term');
            }}
          />
          <button type="submit" disabled={!termText.trim()}>
            {termScanning ? '…' : 'project'}
          </button>
          {/* Explicit stop. A term projection that is still on screen from a
              previous question is indistinguishable from one that failed. */}
          {(termQ || termText) && (
            <button
              type="button"
              className="an-term-clear"
              onClick={() => {
                setTermText('');
                setTermQ(null);
                setTermScan(null);
                setTermHits(new Map());
                setMode('all');
              }}
            >
              clear
            </button>
          )}
        </form>
        {mode === 'mix' && (
          <span className="an-mode-hint">
            balanced delivery: tagged footage only, ≥{MIX_MIN_CATS} categories,
            throttled by the slowest one included
          </span>
        )}
        {/* Which population everything below describes: resolution and frame
            rate tiers, doubtful labels, IMU devices. One menu rather than a row
            of boxes, because none of them is a per-column filter -- each
            changes the population the columns then narrow. */}
        <div className="an-stats-btn an-filters" ref={filtersRef}>
          <button
            type="button"
            className={filtersChanged ? 'on' : ''}
            aria-expanded={filtersOpen}
            aria-haspopup="true"
            onClick={() => setFiltersOpen((o) => !o)}
          >
            Filters{filtersChanged ? ` · ${filtersChanged}` : ''}
            <FiChevronDown className="an-filters-caret" />
          </button>
          {filtersOpen && (
            <div className="an-filters-menu" role="group" aria-label="Filters">
              <label className="an-tier-toggle">
                <input
                  type="checkbox"
                  checked={include720}
                  onChange={(e) => setInclude720(e.target.checked)}
                />
                include 720p
              </label>
              <label className="an-tier-toggle">
                <input
                  type="checkbox"
                  checked={includeLowFps}
                  onChange={(e) => setIncludeLowFps(e.target.checked)}
                />
                include lower fps
              </label>
              <label className="an-tier-toggle" title={contestedNote}>
                <input
                  type="checkbox"
                  checked={hideContested}
                  onChange={(e) => setHideContested(e.target.checked)}
                />
                hide contested labels
                {contestedCount > 0 && (
                  <span className="an-tier-count">{contestedCount.toLocaleString()}</span>
                )}
              </label>
              <label className="an-tier-toggle" title={imuNote}>
                <input
                  type="checkbox"
                  checked={imuOnly}
                  onChange={(e) => setImuOnly(e.target.checked)}
                />
                IMU devices only
              </label>
            </div>
          )}
        </div>
        {/* What the selection HOLDS, as against what it collects. Right-hand end
            of the toolbar because it is a read on the same slice, not a mode. */}
        <button
          type="button"
          className="an-stats-btn an-stats-btn2"
          onClick={() => setReportOpen(true)}
          title="A one-page availability report for the current selection, as copyable text"
        >
          Generate report
        </button>
        <button
          type="button"
          className="an-stats-btn an-stats-btn2"
          onClick={() => setCrossOpen(true)}
          title="Which activities concentrate in which settings — the answer to \u201cwhat do you capture at X?\u201d"
        >
          Where &amp; what
        </button>
        <button
          type="button"
          className="an-stats-btn an-stats-btn2"
          onClick={() => setStatsOpen(true)}
          title="Task and environment mix of the current selection, and how it splits by industry"
        >
          Stats
        </button>
      </div>

      <div className="an-tiles">
        <div className="an-tile">
          <div className="an-tile-header">
            <FiTrendingUp className="an-tile-icon" />
            <h3>Collection rate</h3>
          </div>
          <div className="an-tile-value">
            {!estimate || mixWarming || termWaiting ? (
              mixWarming ? (
                <span className="an-value-none">tagging…</span>
              ) : termWaiting ? (
                <span className="an-value-none">
                  {termScanning ? 'scanning…' : 'waiting for a term'}
                </span>
              ) : (
                '…'
              )
            ) : effRate > 0 ? (
              <>
                ≈{fmtRate(effRate)}
                <span className="an-rate-unit"> h/day</span>
              </>
            ) : (
              <span className="an-value-none">
                {mode === 'mix'
                  ? 'not enough categories'
                  : mode === 'term'
                    ? 'no recent matches from this selection'
                    : 'no recent footage from this selection'}
              </span>
            )}
          </div>
          <div className="an-tile-footer">
            {rateLine}
            {estimate && !mixWarming && !termWaiting && (
              <div className="an-corpus-line">
                {mode === 'mix'
                  ? `a balanced ${mix?.corpus.k || 0}-category corpus already holds ` +
                    `${effCorpus.toFixed(1)}h today`
                  : mode === 'term'
                    ? `matches hold ${(termEstimate?.corpusHours ?? 0).toFixed(1)}h ` +
                      `across ${(termEstimate?.nVideos ?? 0).toLocaleString()} videos` +
                      (termScanning ? ' so far' : ' today')
                    : `${fmtH(estimate.corpusHours)} on hand across ` +
                      `${estimate.nVideos.toLocaleString()} videos` +
                      // Named, because "on hand" is now a growing total rather
                      // than the trailing window the rate above it uses, and
                      // two different spans on one tile have to say so.
                      (estimate.since ? ` · since ${fmtDay(estimate.since)}` : '')}
              </div>
            )}
            {/* One line, not a funnel: the corpus now IS the fleet, minus the
                single filter a lab cares about. Only in "all footage" mode --
                the mix and term rates are subsets of this slice, so pricing
                them against the whole fleet would answer a different question
                than the one asked. */}
            {mode === 'all' && coverage && estimate && (
              <div className="an-cover">
                <div
                  className="an-cover-bar"
                  role="img"
                  aria-label={`${(coverage.share * 100).toFixed(0)}% of fleet capture is ${tierLabel}`}
                >
                  <span style={{ width: `${Math.max(1, coverage.share * 100)}%` }} />
                </div>
                <div className="an-cover-line">
                  <strong>{(coverage.share * 100).toFixed(0)}%</strong> of the
                  fleet's {fmtRate(coverage.fleetPerDay)} h/day
                </div>
                {coverage.unprobed > 0 && (
                  <div className="an-cover-note">
                    still grading {coverage.unprobed.toLocaleString()}{' '}
                    {coverage.unprobed === 1 ? 'video' : 'videos'} — this is a floor
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="an-tile an-tile-eta">
          <div className="an-tile-header">
            <FiClock className="an-tile-icon" />
            <h3>Time to collect</h3>
          </div>
          {/* Every goal at once: the rate is the fact, the goals are just
              divisions of it, and picking one to see hid the others. */}
          <div className="an-proj">
            {GOAL_STOPS.map((g) => {
              const live = effRate > 0 && !mixWarming && !termWaiting;
              const days = live ? g / effRate : null;
              return (
                <div className="an-proj-row" key={g}>
                  <span className="an-proj-goal">{g}h</span>
                  <span className="an-proj-days">
                    {days === null ? '—' : fmtDays(days)}
                  </span>
                  <span className="an-proj-date">
                    {days !== null && days >= 1 && days <= 400
                      ? `by ${dateAfter(days)}`
                      : ''}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="an-tile-footer">
            {effRate > 0 && !mixWarming && !termWaiting
              ? `at the current rate of ${Math.round(effRate).toLocaleString()} h/day`
              : 'projected at the current rate'}
          </div>
        </div>

        {/* How many of the devices behind these numbers have an IMU. Counted
            with every filter except the IMU box, so the tile reads the same
            either way the box is set; a footer appears only when it is on. */}
        <div className="an-tile an-tile-imu">
          <div className="an-tile-header">
            <FiCompass className="an-tile-icon" />
            <h3>IMU devices</h3>
          </div>
          {imuDeviceCounts ? (
            <>
              <div className="an-tile-value">
                {imuDeviceCounts.withImu.toLocaleString()}
              </div>
              <div className="an-goal-line">
                of {imuDeviceCounts.all.toLocaleString()} devices in this selection have an IMU
              </div>
              <div className="an-corpus-line">devices that recorded in the last {estimate?.windowDays} days</div>
            </>
          ) : (
            <div className="an-goal-line">measuring…</div>
          )}
          {imuOnly && (
            <div className="an-tile-footer">
              Filters: showing only these {imuDeviceCounts?.withImu.toLocaleString()} devices
            </div>
          )}
        </div>
      </div>

      <div className="an-card">
        <div className="an-card-title">
          Selection
          <span className="an-card-hint">which slice of the fleet the projection covers</span>
        </div>
        <div className="an-filter-cols">
          {(
            [
              ['Industries', industryRows.map(([k, n]) => [k, k, n] as const),
                excludedInds, setExcludedInds],
              ['Customers', orgRows.map(([id, o]) => [id, o.name, o.n] as const),
                excludedOrgs, setExcludedOrgs],
              ['Tasks', taskRows, excludedCats, setExcludedCats],
              ['Environments', envRows, excludedEnvs, setExcludedEnvs],
            ] as const
          ).map(([title, rows, excluded, setExcluded]) => (
            <div key={title} className="an-filter-col">
              <div className="an-section">
                {title}
                <span className="an-sel">
                  {rows.length - excluded.filter((x) => rows.some(([k]) => k === x)).length}
                  /{rows.length}
                </span>
              </div>
              <div className="an-actions">
                <button type="button" onClick={() => setExcluded([])}>
                  all
                </button>
                <button type="button" onClick={() => setExcluded(rows.map(([k]) => k))}>
                  none
                </button>
              </div>
              <div className="an-list">
                {rows.map(([key, name, n]) => (
                  <label key={key} title={key !== name ? key : undefined}>
                    <input
                      type="checkbox"
                      checked={!excluded.includes(key)}
                      onChange={() => toggle(excluded, setExcluded, key)}
                    />
                    {name}
                    <span className="an-count">{n}</span>
                  </label>
                ))}
                {rows.length === 0 && <div className="an-empty">loading…</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {reportOpen && (
        <ReportPanel
          videos={selectedVideos}
          envs={envs}
          onClose={() => setReportOpen(false)}
        />
      )}

      {crossOpen && (
        <EnvActivityPanel
          videos={selectedVideos}
          envs={envs}
          activityHits={activityHits}
          warming={warming}
          hiddenEnvs={excludedEnvs}
          onClose={() => setCrossOpen(false)}
        />
      )}

      {statsOpen && (
        <StatsPanel
          videos={selectedVideos}
          taskOf={taskOf}
          envs={envs}
          coverage={envCoverage}
          warming={warming}
          onClose={() => setStatsOpen(false)}
        />
      )}
    </div>
  );
}
